// src/forms/auto_init.js
//
// Auto-initialize web tracking when the bundle can resolve an organization and
// the page has no Data Hub element on it.
//
// Loading the bundle alone previously did not construct an instance, so a hosted page or
// landing page WITHOUT a <data-hub-signup-form>/<data-hub-actblue-donate>
// previously got no pageview/analytics tracking at all. Organization context can
// come from data-hub-action's injected window metadata OR from a full-bake,
// org-specific data_hub.js bundle.
//
// The shared/generic bundle is still inert without injected metadata because it
// has no organization_id. A full-bake org bundle intentionally tracks wherever
// it is loaded. Escape hatch: set window.data_hub_disable_auto_init = true before
// the bundle tag.

import { build_organization_metadata } from '../helpers/static_vars';
import { return_organization_metadata } from './data_hub_metadata';

const DATA_HUB_ELEMENT_SELECTOR = 'data-hub-signup-form, data-hub-actblue-donate';

export function resolve_auto_init_organization_metadata(window_reference, compiled_metadata) {
    return return_organization_metadata({}, window_reference, compiled_metadata);
}

/**
 * Pure decision: should the bundle construct a tracking-only instance?
 * Exported for tests.
 * @param {Object} state
 * @param {boolean} state.disable_flag - window.data_hub_disable_auto_init === true
 * @param {*} state.organization_metadata - window.data_hub_organization_metadata
 * @param {boolean} state.has_existing_instance - !!window.data_hub
 * @param {boolean} state.is_loading - window.DataHub.data_hub_loading === true
 * @param {boolean} state.has_data_hub_elements - any element matching DATA_HUB_ELEMENT_SELECTOR
 * @returns {boolean}
 */
export function should_auto_init_tracking({
    disable_flag = false,
    organization_metadata = null,
    has_existing_instance = false,
    is_loading = false,
    has_data_hub_elements = false,
} = {}) {
    if (disable_flag || has_existing_instance || is_loading || has_data_hub_elements) {
        return false;
    }
    return !!(
        organization_metadata
        && typeof organization_metadata === 'object'
        && !Array.isArray(organization_metadata)
        && typeof organization_metadata.organization_id === 'string'
        && organization_metadata.organization_id.trim() !== ''
    );
}

function auto_init_tracking_if_applicable() {
    const organization_metadata = resolve_auto_init_organization_metadata(
        window,
        build_organization_metadata
    );
    const should_init = should_auto_init_tracking({
        disable_flag: window.data_hub_disable_auto_init === true,
        organization_metadata,
        has_existing_instance: !!window.data_hub,
        is_loading: window.DataHub?.data_hub_loading === true,
        has_data_hub_elements: !!document.querySelector(DATA_HUB_ELEMENT_SELECTOR),
    });
    if (!should_init) {
        return;
    }

    if (window.DataHub && typeof window.DataHub.data_hub_loading !== 'undefined') {
        window.DataHub.data_hub_loading = true;
    }
    // Tracking-only instance: organization_id / action domain / theme resolve
    // through the normal injected-metadata > compiled-metadata cascade. It must
    // not add a form loading overlay, form CSS, or ActBlue's third-party script.
    try {
        new window.DataHub.data_hub_class(
            {
                web_settings: { enabled: true },
                inject_data_hub_css: false,
                prefill_all_forms: false,
                block_page_on_user_data_load: false,
                load_actblue: false,
            },
            (instance) => {
                window.data_hub = instance;
                if (window.DataHub && typeof window.DataHub.data_hub_loading !== 'undefined') {
                    window.DataHub.data_hub_loading = false;
                }
            }
        );
    } catch (error) {
        // Reset the flag so a Data Hub element mounted later can still create
        // its own instance instead of waiting on one that will never arrive.
        if (window.DataHub && typeof window.DataHub.data_hub_loading !== 'undefined') {
            window.DataHub.data_hub_loading = false;
        }
        console.error('data_hub auto-init failed:', error);
    }
}

/**
 * Runs the auto-init check one browser task after the DOM is parseable, so the
 * element query is authoritative and an immediately-following manual init wins.
 */
export function schedule_auto_init_tracking() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
    }
    // Always yield one browser task. This lets a script immediately after the
    // bundle perform an explicit manual init first; the constructor then claims
    // window.data_hub and this fallback check becomes a no-op. A microtask is not
    // enough because browsers run a microtask checkpoint between defer scripts.
    const schedule_check = () => setTimeout(auto_init_tracking_if_applicable, 0);
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', schedule_check, { once: true });
    } else {
        schedule_check();
    }
}
